if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      get cart() {
        // Always get fresh reference to handle multiple forms on page
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Check if this is a gift card and the "Send this as a gift" checkbox is checked
        const giftCheckbox = this.form.querySelector('.product-form__gift-checkbox');
        if (giftCheckbox && giftCheckbox.checked) {
          // Get product data for gift card handling
          const productHandle = this.dataset.productHandle;
          if (productHandle) {
            this.handleGiftCardFlow(productHandle, evt);
            return;
          }
        }

        // Delivery date step: for non-exempt products, resolve the delivery date and
        // sync it onto the cart attribute BEFORE adding. requireSelection() returns the
        // already-chosen date immediately, or opens the calendar modal on first add.
        // We always (re)write the attribute here: needsSelection() reads localStorage
        // while the cart/checkout validation reads the `delivery_date` cart attribute,
        // and the two can drift (e.g. a fresh or abandoned cart that still has a stored
        // date). A dateless add is rejected by the validation function, so syncing the
        // attribute first is what lets the add succeed. Exempt products skip this.
        if (!this.isDeliveryExempt() && window.StockedDelivery) {
          const fmt = window.StockedDeliveryFormat;
          // This product's own allowed weeks (already intersected with the current
          // horizon by productAllowedWeeks()), further intersected with the OTHER
          // lines already in the cart -- a non-exempt add must not create a cart no
          // date can satisfy. cartAllowedWeeks() mirrors <cart-delivery-date>'s own
          // data-allowed-weeks output (see assets/delivery-date.js reflectAllowedWeeks):
          // null means the cart is unconstrained, so the second intersection is skipped.
          let productWeeks = this.productAllowedWeeks();

          // A special-date surface spanning several dates (the catering collection's
          // banner tiles): the add goes to the date the customer picked there. There is
          // no default (spec §6.1 step 1) -- with no pick, or a pick this product isn't
          // delivered on, point at the tiles instead of adding.
          const banner = document.body.hasAttribute('data-special-surface')
            ? document.querySelector('special-date-banner')
            : null;
          if (banner && typeof banner.needsTarget === 'function' && banner.needsTarget()) {
            if (!banner.target || productWeeks.indexOf(banner.target) === -1) {
              banner.promptForTarget();
              return;
            }
            productWeeks = [banner.target];
          }

          const cartWeeks = this.cartAllowedWeeks();
          const effectiveWeeks = cartWeeks ? fmt.intersectWeeks([productWeeks, cartWeeks]) : productWeeks;

          // onNoSelection runs when requireSelection() resolves without a date (modal
          // dismissed, or nothing offerable); by default the add is simply dropped.
          // cartEmptiedForAdd: this add's own reconcile just removed the cart's
          // delivery lines (CartOpenPolicy).
          const run = (allowedWeeks = effectiveWeeks, onNoSelection = null, cartEmptiedForAdd = false) => {
            // Whether the calendar modal is about to open for this add (CartOpenPolicy).
            // Asked of the same decision requireSelection() makes: a catering add
            // auto-selects its one date without showing the modal.
            const dateModalWasShown =
              typeof window.StockedDelivery.selectionPromptsModal === 'function'
                ? window.StockedDelivery.selectionPromptsModal({ allowedWeeks })
                : false;
            window.StockedDelivery.requireSelection({ allowedWeeks })
              .then(async (selection) => {
                if (!selection) { // dismissed / no valid dates → do not add
                  if (onNoSelection) onNoSelection();
                  return;
                }
                this.startLoading();
                await window.StockedDelivery.setCartAttributes(selection);
                this.proceedWithSubmission(evt, { dateModalWasShown, cartEmptiedForAdd });
              })
              .catch((error) => {
                console.error('Delivery date selection error:', error);
                this.proceedWithSubmission(evt, { dateModalWasShown, cartEmptiedForAdd });
              });
          };

          // An already-chosen date this add would violate.
          const sel = window.StockedDelivery.getSelection();
          if (sel && effectiveWeeks.indexOf(window.StockedDelivery.slotOf(sel.iso)) === -1) {
            // effectiveWeeks already accounts for every existing cart line (it's the
            // intersection of the product's own weeks with the cart's). If it's
            // non-empty, some OTHER date works for this product AND everything
            // already in the cart -- re-picking needs no removals, so there's
            // nothing for the reconcile dialog to name; just open the constrained
            // calendar directly (same as the "no selection yet" case below already
            // does with no popup). Removal only enters the picture when NOTHING
            // satisfies both the product and the current cart at once, and the
            // product itself is deliverable some week -- i.e. an existing line is
            // the actual blocker.
            if (effectiveWeeks.length || !productWeeks.length) {
              run();
              return;
            }
            const conflicts = window.StockedCartReconcile
              ? window.StockedCartReconcile.conflictingLines(
                  window.StockedDelivery.cartLinesRaw(),
                  productWeeks
                )
              : [];
            if (!conflicts.length) {
              // Nothing existing actually blocks this product's own weeks -- the
              // emptiness of effectiveWeeks traces back to the cart already being
              // internally unsatisfiable independent of this add (revalidate()'s
              // job, not this one). Fall through to the same no-op re-pick.
              run();
              return;
            }
            // productWeeks holds slots: a catering product's is its special date.
            const windowLabel = window.StockedDelivery.slotLabel(productWeeks[0]);
            // Every slot a special date (slotOf() of a special ISO is the ISO itself).
            const specialAdd = productWeeks.every((w) => window.StockedDelivery.slotOf(w) === w);
            // A menu add over a catering cart: the lines being removed are the special
            // date's, so the stored date goes with them and a new one is needed anyway.
            const fromSpecial = window.StockedDelivery.slotOf(sel.iso) === sel.iso;
            window.StockedDelivery.promptReconcile(conflicts, windowLabel).then((choice) => {
              if (choice !== 'confirm') return; // cancel → abort the add, cart & date untouched
              window.StockedDelivery
                .removeLines(conflicts)
                .then(() => {
                  if (!specialAdd && !fromSpecial) {
                    location.reload();
                    return;
                  }
                  // A catering add (spec §6.1 step 3): with the regular lines gone,
                  // commit the special date and finish THIS add rather than reloading
                  // and making the customer click again. The add's own cart response
                  // re-renders the drawer, so it shows the removals too. If the date
                  // can't be committed after all (it closed meanwhile, or the customer
                  // dismissed the calendar between several dates), lines already came
                  // out: resync the page, and say why when the one date just closed.
                  // A menu add over a catering cart (fromSpecial) takes the same path,
                  // committing the date the delivery bar shows first: the menu on screen
                  // is that week's, so a different date could drop the very item being
                  // added. requireSelection() then resolves to it without the calendar,
                  // which opens only when the product can't come that week.
                  window.StockedDelivery.dismissReconcile();
                  const barDate = fromSpecial && !specialAdd ? window.StockedDelivery.barDate() : null;
                  if (barDate && productWeeks.indexOf(window.StockedDelivery.slotOf(barDate.iso)) !== -1) {
                    const committed = window.StockedDelivery.select(barDate.iso);
                    if (committed) {
                      document.dispatchEvent(new CustomEvent('delivery-date:changed', {
                        detail: { date: committed.iso, label: committed.label },
                      }));
                    }
                  }
                  run(productWeeks, () => {
                    if (specialAdd && productWeeks.length === 1) window.StockedDelivery.warnDateNotApplied();
                    else location.reload();
                  }, true);
                })
                .catch((error) => {
                  // A mid-chain removal failure (stale key, network blip, another tab
                  // mutating the cart) can leave the cart PARTIALLY reconciled server-side
                  // -- some lines already gone, others not. Reload regardless so the UI
                  // resyncs with whatever the server actually has, rather than sitting on
                  // a stale page that shows lines which are no longer really there.
                  console.error('Failed to remove conflicting cart lines:', error);
                  location.reload();
                });
            });
            return;
          }

          run();
          return;
        }

        this.proceedWithSubmission(evt);
      }

      isDeliveryExempt() {
        if (this._deliveryExempt !== undefined) return this._deliveryExempt;
        let exempt = false;
        const productHandle = this.dataset.productHandle;
        if (productHandle) {
          try {
            const productDataElement = document.getElementById(`product-data-${productHandle}`);
            if (productDataElement) {
              const productData = JSON.parse(productDataElement.textContent);
              exempt = productData?.product?.weekly_menu_exempt === 1;
            }
          } catch (error) {
            console.error('Error checking product exempt status:', error);
          }
        }
        this._deliveryExempt = exempt;
        return exempt;
      }

      // Slots (week codes, or special-date ISOs for a catering product) this product
      // can be delivered in, intersected with the current lookahead horizon plus open
      // special dates. Always a concrete (possibly empty, possibly horizon-length)
      // array -- never null -- since availableWeeks() resolves an unbounded product to
      // the full horizon rather than "unconstrained".
      productAllowedWeeks() {
        const fmt = window.StockedDeliveryFormat;
        const horizon = window.StockedDelivery.selectableSlots();
        let availability = { weeks: null, start: null, end: null };
        const handle = this.dataset.productHandle;
        if (handle) {
          try {
            const el = document.getElementById(`product-data-${handle}`);
            if (el) {
              const p = JSON.parse(el.textContent)?.product || {};
              availability = fmt.parseAvailability({
                startWeek: p.deliver_start_week,
                endWeek: p.deliver_end_week,
                weeks: p.deliver_weeks,
                dates: p.deliver_dates,
              });
            }
          } catch (error) {
            console.error('Error reading availability:', error);
          }
        }
        return fmt.availableWeeks(availability, horizon);
      }

      // The cart's currently allowed weeks, from the inline <cart-delivery-date>'s own
      // data-allowed-weeks output (see assets/delivery-date.js reflectAllowedWeeks).
      // Absent attribute = unconstrained (null); present = the (possibly empty, i.e.
      // unsatisfiable) intersection of every non-exempt line already in the cart.
      cartAllowedWeeks() {
        const el = document.querySelector('cart-delivery-date:not([data-mode="modal"])');
        const raw = el && el.dataset && el.dataset.allowedWeeks;
        if (!raw) return null;
        try {
          const weeks = JSON.parse(raw);
          return Array.isArray(weeks) ? weeks : null;
        } catch (error) {
          return null;
        }
      }

      proceedWithSubmission(evt, options = {}) {
        this.handleErrorMessage();

        // Capture before the add: the drawer's is-empty class reflects the pre-add
        // cart, and the finally-block below strips it after a successful add.
        const cartWasEmpty = !!(this.cart && this.cart.classList.contains('is-empty'));
        // The swap removed lines without re-rendering the drawer, so is-empty above is
        // stale for it; CartOpenPolicy treats this like an empty cart.
        const cartEmptiedForAdd = !!options.cartEmptiedForAdd;
        const dateModalWasShown = !!options.dateModalWasShown;

        this.startLoading();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              location.reload();
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              const cartResponse = await fetch('/cart.js');
              const fullCartData = await cartResponse.json();

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: fullCartData,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }

            this.error = false;
            const shouldOpen = window.CartOpenPolicy
              ? window.CartOpenPolicy.shouldOpenCart({ cartWasEmpty, cartEmptiedForAdd, dateModalWasShown })
              : true;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response, { open: shouldOpen });
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response, { open: shouldOpen });
              });
              if (!shouldOpen) this.showAddedState();
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      showAddedState() {
        if (this._defaultButtonText === undefined) {
          this._defaultButtonText = this.submitButtonText.textContent;
        }
        this.submitButton.classList.add('added');
        this.submitButtonText.textContent = window.variantStrings.added;
        clearTimeout(this._addedTimer);
        this._addedTimer = setTimeout(() => this.resetAddedState(), 2000);
      }

      resetAddedState() {
        this.submitButton.classList.remove('added');
        if (this._defaultButtonText !== undefined) {
          this.submitButtonText.textContent = this._defaultButtonText;
        }
      }

      startLoading() {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
      }

      handleGiftCardFlow(productHandle, evt) {
        // Get the gift card selector component
        const giftCardSelector = document.querySelector('gift-card-selector');
        if (!giftCardSelector) {
          console.error('Gift card selector not found');
          // Fallback to normal submission
          this.proceedWithSubmission(evt);
          return;
        }

        // Get product data
        const productDataElement = document.getElementById(`product-data-${productHandle}`);
        if (!productDataElement) {
          console.error('Product data not found for handle:', productHandle);
          this.proceedWithSubmission(evt);
          return;
        }

        try {
          const productData = JSON.parse(productDataElement.textContent);

          // Get the selected variant
          let selectedVariant = null;
          const selectedVariantId = parseInt(this.variantIdInput.value);
          
          // Check if variants exist in the product data structure
          if (productData.product && productData.product.variants) {
            selectedVariant = productData.product.variants.find(v => v.id === selectedVariantId);
          } else if (productData.variants) {
            selectedVariant = productData.variants.find(v => v.id === selectedVariantId);
          }

          // If variant not found in data, create a basic variant object from form
          if (!selectedVariant) {
            // Try to get variant data from the page's variant data
            const variantScripts = document.querySelectorAll('[data-variant-id]');
            for (const script of variantScripts) {
              if (script.dataset.variantId == selectedVariantId) {
                try {
                  const variantData = JSON.parse(script.textContent);
                  selectedVariant = variantData;
                  break;
                } catch (e) {
                  // Continue to next script
                }
              }
            }
            
            // Final fallback - create basic variant object
            if (!selectedVariant) {
              selectedVariant = {
                id: selectedVariantId,
                title: document.querySelector('.product__title')?.textContent || 'Gift Card',
                price: document.querySelector('[data-price]')?.dataset.price || 0
              };
            }
          }


          // Collect form data
          const formData = new FormData(this.form);
          
          // Show the gift card selector
          giftCardSelector.showGiftCardForm(productData.product || productData, selectedVariant, formData);
          
        } catch (error) {
          console.error('Error parsing product data:', error);
          this.proceedWithSubmission(evt);
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
